import { useMemo, useState } from "react";
import { inr } from "../lib/api";
import { buildAxes, defaultSelection, resolveVariant, applyAxisChange } from "../lib/variantAxes";
import VariantAxisPicker from "./VariantAxisPicker";

/**
 * ProductVariantPicker — axis-based bottom-sheet.
 *
 * Historically rendered a flat radio list of every variant, which
 * jumbled Color + Size chips into one row. iter116 groups the
 * variants into axes (Color / Size / Custom) via ``buildAxes`` and
 * renders one row per axis. In matrix mode (variants carry
 * ``option_values``) both axes must be picked to enable Add-to-cart;
 * in legacy flat mode picking any chip is enough.
 */
export default function ProductVariantPicker({ product, onConfirm, onClose }) {
    const variants = useMemo(
        () => (product?.variants || []).filter((v) => v?.enabled !== false),
        [product],
    );
    const axes = useMemo(() => buildAxes(variants), [variants]);
    const [selection, setSelection] = useState(() => defaultSelection(variants, axes));
    const [busy, setBusy] = useState(false);

    const selectedVariant = useMemo(
        () => resolveVariant(variants, axes, selection),
        [variants, axes, selection],
    );

    if (!variants.length) return null;

    const setAxis = (axisKey, value) => {
        setSelection((prev) => applyAxisChange(variants, axes, prev, axisKey, value));
    };

    const confirm = async () => {
        if (!selectedVariant || busy) return;
        setBusy(true);
        try {
            await onConfirm?.(selectedVariant);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm"
            data-testid={`variant-picker-${product.id}`}
            onClick={onClose}
        >
            <div
                className="w-full max-w-md md:max-w-lg bg-card rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="pt-3 pb-1 flex justify-center">
                    <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
                </div>
                <div className="px-5 pt-2 pb-3 border-b border-border">
                    <h3 className="font-semibold text-base">Choose an option</h3>
                    <p className="text-xs text-muted-foreground line-clamp-1">{product.name}</p>
                </div>

                <div className="overflow-y-auto flex-1 p-5">
                    <VariantAxisPicker
                        axes={axes}
                        variants={variants}
                        selection={selection}
                        onChange={setAxis}
                    />
                </div>

                <div className="p-4 border-t border-border bg-card">
                    {selectedVariant && (
                        <div className="mb-3 flex items-baseline justify-between">
                            <span className="text-sm text-muted-foreground">Selected</span>
                            <div className="flex items-baseline gap-2">
                                <span className="font-bold" data-testid="variant-selected-price">{inr(selectedVariant.selling_price)}</span>
                                {selectedVariant.mrp > selectedVariant.selling_price && (
                                    <span className="text-xs text-muted-foreground line-through">
                                        {inr(selectedVariant.mrp)}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    <button
                        data-testid={`confirm-variant-${product.id}`}
                        disabled={!selectedVariant || busy}
                        onClick={confirm}
                        className="w-full bg-primary text-primary-foreground font-bold py-3 rounded-xl active:scale-95 transition disabled:opacity-40"
                    >
                        {busy ? "Adding…" : selectedVariant ? "Add to cart" : "Pick every option"}
                    </button>
                </div>
            </div>
        </div>
    );
}
